import { faCommentCheck, faCommentQuestion } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Grid, styled, Tooltip, Typography } from '@mui/material'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { CampaignVideoQuizResults } from '@/api/campaigns/campagin-quiz-results'
import { theme } from '@/theme/theme'

const getScoreColor = (score: number) => {
  if (score <= 50) return theme.palette.pink[800]
  return theme.palette.green[500]
}

const TruncatedTextWithTooltip: FC<{ text: string; isBold?: boolean }> = ({ text, isBold = false }) => {
  const needsTruncation = text.length > 70

  // Find the last space before the 70 character limit
  const truncatedText = needsTruncation ? text.substring(0, text.lastIndexOf(' ', 70)) + '...' : text
  const fontWeight = isBold ? theme.typography.fontWeightSemiBold : 'normal'

  if (!needsTruncation) {
    return <Typography fontWeight={fontWeight}>{text}</Typography>
  }

  return (
    <Tooltip title={text} arrow>
      <span>
        <Typography fontWeight={fontWeight}>{truncatedText}</Typography>
      </span>
    </Tooltip>
  )
}

const QuizResults: FC<{ quizResults: CampaignVideoQuizResults; score: number }> = ({ quizResults, score }) => {
  const { t } = useTranslation()

  return (
    <Box display="flex" justifyContent="space-between" gap={6}>
      <Box flex={1}>
        {quizResults?.map((question, i) => {
          return (
            <Grid key={i} container paddingTop={4} spacing={6}>
              <Grid item xs={3}>
                <Box display="flex" flexDirection="column" justifyContent="center" paddingTop={1}>
                  <StyledProgressBar>
                    <StyledProgressBarFill score={question.score} />
                  </StyledProgressBar>
                  <Box display="flex" gap={2} alignItems="center">
                    <Typography fontSize={20} fontWeight={theme.typography.fontWeightSemiBold}>
                      {question.score >= 0 && question.score + '%'}
                    </Typography>
                    <Typography>{'(' + question.correct_answered + '/' + question.total_answered + ')'}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={9}>
                <Box display="flex" gap={1} alignItems="center" paddingBottom={1}>
                  <FontAwesomeIcon icon={faCommentQuestion}></FontAwesomeIcon>
                  <TruncatedTextWithTooltip text={question.question} />
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                  <FontAwesomeIcon icon={faCommentCheck} color={theme.palette.green[900]}></FontAwesomeIcon>
                  <TruncatedTextWithTooltip text={question.correct_answer} isBold />
                </Box>
              </Grid>
            </Grid>
          )
        })}
      </Box>
      <Box display="flex" flexDirection="column" alignItems="center" gap={2} paddingTop={3}>
        <Typography fontWeight={theme.typography.fontWeightBold}>
          {t('campaignDashboard.campaignQuizResults.averageQuizScore')}
        </Typography>
        <StyledAverageQuizScoreBox score={score || 0}>
          <Typography fontWeight={theme.typography.fontWeightBold} fontSize={80}>
            {score.toFixed()}
          </Typography>
        </StyledAverageQuizScoreBox>
      </Box>
    </Box>
  )
}

const StyledProgressBar = styled(Box)(() => ({
  width: '100%',
  background: theme.palette.grey[300],
  height: theme.spacing(1),
  borderRadius: 10,
  marginBottom: theme.spacing(1),
}))

const StyledAverageQuizScoreBox = styled(Box)(({ score }: { score: number }) => ({
  minWidth: theme.spacing(21),
  padding: theme.spacing(6, 4),
  border: '2px solid ' + getScoreColor(score),
  borderRadius: '10px',
  display: 'flex',
  justifyContent: 'center',
  alignContent: 'center',
}))

const StyledProgressBarFill = styled(Box)(({ score }: { score: number }) => ({
  width: (score > 0 ? score : 0) + '%',
  background: getScoreColor(score),
  height: theme.spacing(1),
  borderRadius: 10,
}))

export default QuizResults
